/**
 * Tradução da forma crua da API (inglês nos tokens de enum, pt-BR nos
 * campos) para o domínio pt-BR da tela (`TP-052`, T9). Roda UMA vez, logo
 * depois do fetch — ver `api/tipos.ts`.
 */

import type {
  AcaoDeDecisao,
  AcaoDeDecisaoApi,
  Decisao,
  DecisaoDetalheApi,
  ConferenciaImportada,
  ConferenciaImportadaApi,
  ConferenciaResumo,
  ConferenciaResumoApi,
  DetalheConferenciaApi,
  DetalheDaConferencia,
  EstadoDaConferencia,
  EstadoDaConferenciaApi,
  EstadoDaLinha,
  GuiaDetalhada,
  GuiaDetalheApi,
  GuiaResumo,
  GuiaResumoApi,
  LinhaDetalheApi,
  LinhaDoVeredito,
  Motivo,
  MotivoFaturamentoApi,
} from './tipos';

const ESTADO_DA_CONFERENCIA: Record<EstadoDaConferenciaApi, EstadoDaConferencia> = {
  UnderReview: 'em_conferencia',
  Secured: 'segurada',
  Released: 'liberada',
  Rejected: 'recusada',
};

export function mapearEstadoDaConferencia(bruto: EstadoDaConferenciaApi): EstadoDaConferencia {
  return ESTADO_DA_CONFERENCIA[bruto];
}

const ACAO: Record<AcaoDeDecisaoApi, AcaoDeDecisao> = {
  Release: 'liberar',
  CorrectedInIw: 'corrigido_no_iw',
};

/** Inverso de `ACAO` — o envio (`POST /decisoes`) manda o NOME do membro em inglês. */
export const ACAO_PARA_API: Record<AcaoDeDecisao, AcaoDeDecisaoApi> = {
  liberar: 'Release',
  corrigido_no_iw: 'CorrectedInIw',
};

const ESTADO_DA_LINHA: Record<string, EstadoDaLinha> = {
  Ok: 'ok',
  Warning: 'alerta',
  Blocked: 'barrado',
  NotCovered: 'sem_cobertura',
};

/** `estado` da linha é `string` livre no contrato (ver `api/tipos.ts`) — token não mapeado vira `desconhecido`, nunca `ok` por omissão (`D7` do `BR-008`). */
function mapearEstadoDaLinha(bruto: string | null): EstadoDaLinha {
  if (bruto === null) return 'desconhecido';
  return ESTADO_DA_LINHA[bruto] ?? 'desconhecido';
}

export function mapearGuiaResumo(bruto: GuiaResumoApi): GuiaResumo {
  return {
    referenciaGuia: bruto.referenciaGuia ?? '—',
    avaliacaoId: bruto.avaliacaoId,
    ok: bruto.ok,
    alerta: bruto.alerta,
    barrado: bruto.barrado,
    semCobertura: bruto.semCobertura,
  };
}

export function mapearConferenciaImportada(bruto: ConferenciaImportadaApi): ConferenciaImportada {
  return {
    conferenciaId: bruto.conferenciaId,
    estado: mapearEstadoDaConferencia(bruto.estado),
    motivoRecusa: bruto.motivoRecusa,
    guias: (bruto.guias ?? []).map(mapearGuiaResumo),
  };
}

export function mapearConferenciaResumo(bruto: ConferenciaResumoApi): ConferenciaResumo {
  return {
    id: bruto.id,
    contratoId: bruto.contratoId,
    competencia: bruto.competencia,
    estado: mapearEstadoDaConferencia(bruto.estado),
    motivoRecusa: bruto.motivoRecusa,
    criadoEm: bruto.criadoEm,
    ok: bruto.ok,
    alerta: bruto.alerta,
    barrado: bruto.barrado,
    semCobertura: bruto.semCobertura,
  };
}

function mapearMotivo(bruto: MotivoFaturamentoApi): Motivo {
  return { regraContratualId: bruto.regraContratualId, mensagem: bruto.mensagem, fonte: bruto.fonte };
}

function mapearLinha(bruto: LinhaDetalheApi): LinhaDoVeredito {
  return {
    linha: bruto.linha,
    codigo: bruto.codigo ?? '—',
    estado: mapearEstadoDaLinha(bruto.estado),
    motivos: (bruto.motivos ?? []).map(mapearMotivo),
  };
}

function mapearGuiaDetalhada(bruto: GuiaDetalheApi): GuiaDetalhada {
  return {
    referenciaGuia: bruto.referenciaGuia ?? '—',
    avaliacaoId: bruto.avaliacaoId,
    linhas: (bruto.linhas ?? []).map(mapearLinha),
  };
}

function mapearDecisao(bruto: DecisaoDetalheApi): Decisao {
  return {
    avaliacaoId: bruto.avaliacaoId,
    linha: bruto.linha,
    acao: ACAO[bruto.acao],
    motivo: bruto.motivo ?? '',
    decididoPor: bruto.decididoPor,
    decididoEm: bruto.decididoEm,
  };
}

export function mapearDetalheDaConferencia(bruto: DetalheConferenciaApi): DetalheDaConferencia {
  return {
    id: bruto.id,
    contratoId: bruto.contratoId,
    competencia: bruto.competencia,
    estado: mapearEstadoDaConferencia(bruto.estado),
    motivoRecusa: bruto.motivoRecusa,
    guias: (bruto.guias ?? []).map(mapearGuiaDetalhada),
    decisoes: (bruto.decisoes ?? []).map(mapearDecisao),
  };
}
